import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import DataService from '../../services/DataService';
import useNotification from '../../hooks/useNotification';
import NotificationSystem from '../../components/common/NotificationSystem';

const UnpaidBills = () => {
    const [unpaidBills, setUnpaidBills] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const { notification, showNotification, hideNotification } = useNotification();

    const fetchUnpaidBills = useCallback(async () => {
        try {
            const bills = await DataService.filter('bills', { status: 'Sent' });

            const billsWithDetails = await Promise.all(bills.map(async (bill) => {
                
                const quote = await DataService.getById('quotes', bill.quoteId);
                if (!quote) {
                    console.log('Devis non trouvé pour:', bill.billCustomId);
                    return null;
                }

                const opportunity = await DataService.getById('opportunities', quote.opportunityId);
                if (!opportunity) {
                    console.log('Opportunité non trouvée pour:', bill.billCustomId);
                    return null;
                }

                const account = await DataService.getById('accounts', opportunity.accountId);
                if (!account) {
                    console.log('Compte non trouvé pour:', bill.billCustomId);
                    return null;
                }

                // Convertir l'objet timeStamp en Date
                const billDate = new Date(
                    parseInt(bill.timeStamp.year),
                    parseInt(bill.timeStamp.month) - 1, // Les mois JavaScript commencent à 0
                    parseInt(bill.timeStamp.day),
                    parseInt(bill.timeStamp.hour || 0),
                    parseInt(bill.timeStamp.minute || 0)
                );

                const today = new Date();
                const diffTime = Math.abs(today - billDate);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                // S'assurer que le montant est un nombre valide
                const amount = typeof bill.currentBillAmount === 'number' ? bill.currentBillAmount : 0;

                return {
                    accountName: account.name,
                    opportunityName: opportunity.name,
                    amount: amount,
                    daysLate: diffDays
                };
            }));

            const filteredBills = billsWithDetails.filter(bill => bill !== null);
            setUnpaidBills(filteredBills);

            // Vérifier les factures en retard
            const lateBills = filteredBills.filter(bill => bill.daysLate > 2);
            
            if (lateBills.length > 0) {  
                showNotification(
                    `${lateBills.length} facture${lateBills.length > 1 ? 's' : ''} en retard de plus de 2 jours`,
                    'error'
                );
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des factures impayées:', error);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchUnpaidBills();
    }, [fetchUnpaidBills]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedBills = () => {
        if (!sortConfig.key) return unpaidBills;

        return [...unpaidBills].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'account':
                    aValue = a.accountName;
                    bValue = b.accountName;
                    break;
                case 'project':
                    aValue = a.opportunityName;
                    bValue = b.opportunityName;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return faSort;
        }
        return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
    };

    return (
        <div className="panel">
            {notification.isVisible && (
                <NotificationSystem
                    message={notification.message}
                    type={notification.type}
                    onClose={hideNotification}
                />
            )}
            <div className="panel-heading">
                <div className="panel-header-content">
                    <span className="icon-text">
                        <span className="icon">
                            <FontAwesomeIcon icon={faFileInvoiceDollar} />
                        </span>
                        <span> Factures impayées</span>
                    </span>
                </div>
            </div>
            <div className="panel-block">
                <div className="table-container">
                    <table className="table is-fullwidth is-striped is-hoverable mb-0">
                        <thead>
                            <tr>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('account')}
                                    >
                                        Compte <FontAwesomeIcon icon={getSortIcon('account')} className="ml-1" />
                                    </button>
                                </th>
                                <th>
                                    <button 
                                        className="button table-sort-button"
                                        onClick={() => handleSort('project')}
                                    >
                                        Projet <FontAwesomeIcon icon={getSortIcon('project')} className="ml-1" />
                                    </button>
                                </th>
                                <th className="has-text-right">Montant</th>
                                <th className="has-text-right">Retard (jours)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getSortedBills().map((bill, index) => (
                                <tr key={index}>
                                    <td>{bill.accountName}</td>
                                    <td>{bill.opportunityName}</td>
                                    <td className="has-text-right">
                                        {bill.amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                                    </td>
                                    <td className="has-text-right">{bill.daysLate || 0}</td>
                                </tr>
                            ))}
                            {unpaidBills.length === 0 && (
                                <tr>
                                    <td colSpan="4" className="has-text-centered">Aucune facture impayée</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="panel-footer"></div>
        </div>
    );
};

export default UnpaidBills;
